import React from "react";

import { StartingPitcher } from "../types";
import {
  formatPitchingRate,
  formatInningsPitched,
  formatCountingStat,
} from "../../../shared/utils/formatters";

interface PitcherCardProps {
  teamName: string;
  pitcher: StartingPitcher | null;
}

/**
 * Displays a starting pitcher with season-to-date pitching stats.
 * Shows a compact stat row with key pitching categories.
 */
export function PitcherCard({ teamName, pitcher }: PitcherCardProps): JSX.Element {
  if (!pitcher) {
    return (
      <div className="pitcher-section">
        <h4 className="pitcher-section-title">{teamName}</h4>
        <p className="pitcher-unavailable">Starting pitcher not yet announced</p>
      </div>
    );
  }

  const s = pitcher.season_to_date_pitching;
  const displayName = pitcher.player_name ?? "Name unavailable";

  return (
    <div className="pitcher-section">
      <h4 className="pitcher-section-title">{teamName}</h4>
      <div className="pitcher-info">
        <span className="pitcher-name">{displayName}</span>
        {pitcher.throws_hand && (
          <span className="pitcher-hand"> ({pitcher.throws_hand}HP)</span>
        )}
      </div>
      {s ? (
        <div className="pitcher-stats-grid">
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatPitchingRate(s.era)}</span>
            <span className="pitcher-stat-label">ERA</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatPitchingRate(s.whip)}</span>
            <span className="pitcher-stat-label">WHIP</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">
              {formatCountingStat(s.wins)}–{formatCountingStat(s.losses)}
            </span>
            <span className="pitcher-stat-label">W-L</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatInningsPitched(s.innings_pitched)}</span>
            <span className="pitcher-stat-label">IP</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatCountingStat(s.strike_outs)}</span>
            <span className="pitcher-stat-label">SO</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatCountingStat(s.base_on_balls)}</span>
            <span className="pitcher-stat-label">BB</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatCountingStat(s.games_started)}</span>
            <span className="pitcher-stat-label">GS</span>
          </div>
          <div className="pitcher-stat">
            <span className="pitcher-stat-value">{formatCountingStat(s.quality_starts)}</span>
            <span className="pitcher-stat-label">QS</span>
          </div>
        </div>
      ) : (
        <p className="pitcher-unavailable">Season stats not yet available</p>
      )}
    </div>
  );
}
